import { type EngineEventMap } from '../core/EngineEvents';
import { type EventEmitter } from '../events/EventEmitter';

/** Construction options for {@link Fullscreen}. */
export interface FullscreenOptions {
  /**
   * Element that fills the screen. Omit to leave fullscreen unavailable.
   *
   * The host names it because only the host knows what "the game" means on its page. A host whose
   * canvas is the entire page passes `document.documentElement`, which is what the player's own
   * fullscreen key does and the only choice that keeps sibling HTML layers visible. A host embedding
   * the canvas in a larger document passes the element that wraps it instead.
   */
  readonly element?: HTMLElement | undefined;
  /** Bus `fullscreenChanged` is published on. */
  readonly events: EventEmitter<EngineEventMap>;
}

/**
 * Puts one element on the whole screen, and is the one place that knows whether it is there.
 *
 * The browser is the other half of this: the player can leave with a key, a gesture or a tab switch,
 * and nothing asks first. So the state is never inferred from what was requested, only ever read back
 * from the document, and `fullscreenChanged` is what a host listens to rather than remembering what it
 * last asked for.
 *
 * Entering is never automatic. It is a mode change that takes the screen away from everything else on
 * the machine, and browsers enforce that by refusing any request that is not spending a real
 * interaction. A caller that asks outside one gets `false`, not an exception.
 *
 * Only the standard API is used. The vendor-prefixed spelling would add browsers that stopped updating
 * before 2023, and the cost of carrying it is paid on every read of this file forever.
 *
 * A context built without an element, as every test harness is, gets an instance that supports nothing
 * and does nothing.
 */
export class Fullscreen {
  readonly #element: HTMLElement | undefined;
  readonly #events: EventEmitter<EngineEventMap>;
  readonly #document: Document | undefined;

  #active: boolean;
  #disposed = false;

  constructor(options: FullscreenOptions) {
    this.#element = options.element;
    this.#events = options.events;
    this.#document = options.element?.ownerDocument;

    this.#active = this.#matches();
    this.#document?.addEventListener('fullscreenchange', this.#onFullscreenChange);
  }

  /** Whether this element is filling the screen right now. */
  get isActive(): boolean {
    return this.#active;
  }

  /**
   * Whether asking could succeed in this document.
   *
   * False on a browser without the API, and equally false where the API exists but the document is not
   * allowed to use it, which is what an iframe without the `fullscreen` permission looks like. Both are
   * the same answer to a host deciding whether to draw a control at all: there is no point offering one.
   *
   * A hint rather than a promise. {@link Fullscreen.request} is still the only thing that knows whether
   * a particular attempt was granted.
   */
  get isSupported(): boolean {
    const element = this.#element;
    if (element === undefined || typeof element.requestFullscreen !== 'function') {
      return false;
    }
    return this.#document?.fullscreenEnabled === true;
  }

  /**
   * Asks the browser to fill the screen, and resolves with whether it did.
   *
   * A refusal is ordinary rather than exceptional: no interaction to spend, a permission the document
   * does not have, or a device that only ever does this for video. Nothing here retries, because a
   * second attempt fails for the same reason the first one did. A caller that gets `false` should carry
   * on windowed.
   */
  async request(): Promise<boolean> {
    const element = this.#element;
    if (element === undefined || this.#disposed || !this.isSupported) {
      return false;
    }
    if (this.#matches()) {
      return true;
    }

    try {
      await element.requestFullscreen();
    } catch {
      return false;
    }

    this.#refresh();
    return this.#active;
  }

  /** Gives the screen back, if this element is the one holding it. Does nothing otherwise. */
  exit(): void {
    if (!this.#matches()) {
      return;
    }
    // A refused exit leaves the screen where it was, so the state is re-read rather than assumed.
    void this.#document?.exitFullscreen().catch(() => {
      this.#refresh();
    });
  }

  /** Stops listening to the document. Does not leave fullscreen: the page may outlive the engine. */
  dispose(): void {
    if (this.#disposed) {
      return;
    }
    this.#disposed = true;
    this.#document?.removeEventListener('fullscreenchange', this.#onFullscreenChange);
  }

  #matches(): boolean {
    return this.#element !== undefined && this.#document?.fullscreenElement === this.#element;
  }

  #refresh(): void {
    if (this.#disposed) {
      return;
    }

    const isActive = this.#matches();
    if (isActive === this.#active) {
      return;
    }
    this.#active = isActive;
    this.#events.emit('fullscreenChanged', { isActive });
  }

  readonly #onFullscreenChange = (): void => {
    this.#refresh();
  };
}
